/**
 * Trend Chart Component
 *
 * Displays period-over-period comparison as a simple metric card.
 * Shows revenue change with visual indicators.
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/frontend/components/ui/card";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import type { PeriodComparison, SalespersonPeriodComparison } from "@/shared/types/sales-analytics";

interface TrendChartProps {
  comparison?: PeriodComparison | SalespersonPeriodComparison;
  title?: string;
  description?: string;
}

export function TrendChart({ comparison, title = "Period Comparison", description }: TrendChartProps) {
  if (!comparison) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title}</CardTitle>
          {description && <CardDescription>{description}</CardDescription>}
        </CardHeader>
        <CardContent>
          <div className="text-muted-foreground">No comparison data available</div>
        </CardContent>
      </Card>
    );
  }

  // Handle both PeriodComparison and SalespersonPeriodComparison types
  const changeAmount = 'changeAmount' in comparison
    ? comparison.changeAmount
    : comparison.revenueChangeAmount;
  const changePercentage = 'changePercentage' in comparison
    ? comparison.changePercentage
    : comparison.revenueChangePercentage;
  const previousPeriod = 'previousPeriod' in comparison ? comparison.previousPeriod : null;

  // Determine trend direction
  const isPositive = changeAmount > 0;
  const isNegative = changeAmount < 0;

  const TrendIcon = isPositive ? TrendingUp : isNegative ? TrendingDown : Minus;
  const trendColor = isPositive
    ? "text-green-600"
    : isNegative
    ? "text-red-600"
    : "text-gray-600";

  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
        {description && <CardDescription>{description}</CardDescription>}
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Change indicator */}
        <div className="flex items-center gap-4">
          <TrendIcon className={`h-8 w-8 ${trendColor}`} />
          <div>
            <div className={`text-3xl font-bold ${trendColor}`}>
              {isPositive ? '+' : ''}{changePercentage.toFixed(1)}%
            </div>
            <div className="text-sm text-muted-foreground">
              {isPositive ? 'Increase' : isNegative ? 'Decrease' : 'No change'} from previous period
            </div>
          </div>
        </div>

        {/* Details */}
        {previousPeriod && (
          <div className="grid grid-cols-2 gap-4 pt-4 border-t">
            <div>
              <div className="text-sm text-muted-foreground">Previous Period</div>
              <div className="text-lg font-semibold">
                ฿{previousPeriod.totalRevenue.toLocaleString()}
              </div>
              <div className="text-xs text-muted-foreground">
                {previousPeriod.projectCount} projects
              </div>
            </div>
            <div>
              <div className="text-sm text-muted-foreground">Change Amount</div>
              <div className={`text-lg font-semibold ${trendColor}`}>
                {isPositive ? '+' : ''}฿{changeAmount.toLocaleString()}
              </div>
              <div className="text-xs text-muted-foreground">
                Period: {previousPeriod.year}-{String(previousPeriod.month).padStart(2, '0')}
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
